import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PRESETS } from "../lib/exportPresets";

// Admin Questions page — write / edit / publish Q&A pages that live at /ask/{slug}.
// Each answer can optionally link to a preset (e.g. answer about Asana →
// related_preset: 'asana' → renders an inline "Use the Asana preset" button on
// the public page).
//
// If the URL has ?from_lead=...&title=...&summary=..., we pre-fill the form
// so the Leads page can hand off a draft in one click.

const EMPTY = { question_id: null, title: "", slug: "", summary: "", body_md: "", related_preset: "", status: "draft", source_lead_id: null };

export default function AdminQuestions() {
    const [questions, setQuestions] = useState([]);
    const [editing, setEditing] = useState(null); // EMPTY shape or a row
    const [loading, setLoading] = useState(true);
    const [params] = useSearchParams();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/questions");
            setQuestions(data.questions || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    // Pre-fill from lead if URL has the params (one-click handoff from /admin/leads).
    useEffect(() => {
        const fromLead = params.get("from_lead");
        if (fromLead && !editing) {
            setEditing({
                ...EMPTY,
                source_lead_id: fromLead,
                title: params.get("title") || "",
                summary: params.get("summary") || "",
                body_md: `## Quick answer\n\n_Write the 2-sentence TL;DR here._\n\n## Why this happens\n\n_Explain the technical cause briefly._\n\n## Fix it with OpenCSV\n\n1. Open https://opencsv.io/app\n2. Drag your CSV in\n3. Click **Export → \`[PICK A PRESET]\`**\n4. Re-upload to the destination tool\n\n## Why this preset works\n\n_Explain the byte-level difference: BOM, line endings, quote style._\n`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const save = async () => {
        if (!editing.title.trim()) { toast.error("Title required"); return; }
        try {
            if (editing.question_id) {
                await api.patch(`/admin/questions/${editing.question_id}`, editing);
                toast.success("Updated");
            } else {
                await api.post("/admin/questions", editing);
                toast.success("Created");
            }
            setEditing(null);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const remove = async (q) => {
        if (!window.confirm(`Delete "${q.title}"?`)) return;
        await api.delete(`/admin/questions/${q.question_id}`);
        toast.success("Deleted");
        refresh();
    };

    const publishedCount = useMemo(() => questions.filter((q) => q.status === "published").length, [questions]);

    return (
        <>
            <Helmet><title>Admin · Q&amp;A · OpenCSV</title></Helmet>
            <section className="px-6 sm:px-12 py-12 bg-paper min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ admin · q&amp;a</div>
                            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-1">Q&amp;A pages</h1>
                            <p className="text-sm text-ink-soft mt-1">{publishedCount} published · {questions.length - publishedCount} draft</p>
                        </div>
                        <div className="flex gap-2 text-sm">
                            <Link to="/admin/leads" className="border border-ink/20 px-3 py-2" data-testid="admin-questions-leads-link">Leads</Link>
                            <Link to="/admin" className="border border-ink/20 px-3 py-2">Users</Link>
                            <button onClick={() => setEditing({ ...EMPTY })} className="btn-brutal bg-cobalt text-white border border-cobalt px-3 py-2 inline-flex items-center gap-1.5" data-testid="admin-questions-new"><Plus size={14} /> New question</button>
                        </div>
                    </div>

                    {editing && (
                        <div className="bg-white border border-ink/15 p-6 mb-8" data-testid="question-editor">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Title (= the question)</label>
                                    <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2" data-testid="question-title" placeholder='e.g. "Why does Asana refuse my CSV?"' />
                                </div>
                                <div>
                                    <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Slug (URL)</label>
                                    <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 font-mono text-sm" data-testid="question-slug" placeholder="auto-generated if blank" />
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Summary (2 sentences — shown in /ask hub + Google snippet)</label>
                                <textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 text-sm" rows={2} data-testid="question-summary" />
                            </div>
                            <div className="mt-4">
                                <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Body (Markdown supported — headings, lists, **bold**, `code`, [links](url))</label>
                                <textarea value={editing.body_md} onChange={(e) => setEditing({ ...editing, body_md: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-3 font-mono text-sm" rows={16} data-testid="question-body" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Related preset (optional)</label>
                                    <select value={editing.related_preset || ""} onChange={(e) => setEditing({ ...editing, related_preset: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2" data-testid="question-preset">
                                        <option value="">— none —</option>
                                        {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="ui-heading text-[11px] uppercase tracking-widest text-ink-soft">Status</label>
                                    <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2" data-testid="question-status">
                                        <option value="draft">Draft (not public)</option>
                                        <option value="published">Published (public + indexable)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-5 flex justify-end gap-2">
                                <button onClick={() => setEditing(null)} className="border border-ink/20 px-3 py-2 text-sm" data-testid="question-cancel">Cancel</button>
                                <button onClick={save} className="btn-brutal bg-ink text-white border border-ink px-4 py-2 ui-heading text-sm font-semibold" data-testid="question-save">Save</button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white border border-ink/15">
                        {loading ? (
                            <div className="p-8 text-center text-sm text-ink-muted">Loading…</div>
                        ) : questions.length === 0 ? (
                            <div className="p-8 text-center text-sm text-ink-muted">No questions yet. Click <em>New question</em> or use <em>Draft answer</em> on a lead.</div>
                        ) : (
                            questions.map((q) => (
                                <div key={q.question_id} className="p-4 border-b border-ink/10 last:border-b-0 flex items-center justify-between gap-4 flex-wrap" data-testid={`question-${q.question_id}`}>
                                    <div className="flex-1 min-w-[280px]">
                                        <div className="flex items-center gap-2 mb-1 text-xs">
                                            <span className={`px-1.5 py-0.5 ui-heading uppercase tracking-wider ${q.status === "published" ? "bg-cobalt text-white" : "bg-ink/10 text-ink-muted"}`}>{q.status}</span>
                                            <span className="text-ink-muted font-mono">/ask/{q.slug}</span>
                                            <span className="text-ink-muted">{q.view_count || 0} views</span>
                                        </div>
                                        <div className="font-medium">{q.title}</div>
                                    </div>
                                    <div className="flex gap-2">
                                        {q.status === "published" && (
                                            <a href={`/ask/${q.slug}`} target="_blank" rel="noreferrer" className="border border-ink/20 px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`question-view-${q.question_id}`}><ExternalLink size={11} /> View</a>
                                        )}
                                        <button onClick={() => setEditing({ ...EMPTY, ...q, related_preset: q.related_preset || "" })} className="border border-ink/20 px-3 py-1.5 text-xs" data-testid={`question-edit-${q.question_id}`}>Edit</button>
                                        <button onClick={() => remove(q)} className="border border-signal-red/30 text-signal-red px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`question-delete-${q.question_id}`}><Trash2 size={11} /> Delete</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
